import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { getV2TenantGroups, type V2TenantGroupListResponse } from '@/api/white-labels';
import { createSchoolNetwork, attachSchoolToNetwork } from '@/api/school-networks';
import {
  PhysicalNetworkSelector,
  defaultPhysicalNetworkChoice,
  type PhysicalNetworkChoice,
} from '@/components/schools/PhysicalNetworkSelector';
import { attachWarningText } from '@/components/school-networks/AttachSchoolDialog';
import type { SchoolEditData } from '@/types/school';
import { AlertTriangle, ExternalLink, Globe, Info, Link2, Loader2, Network, Server, Tag } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface NetworkCardProps {
  school: SchoolEditData;
  onSaved: () => void;
}

const IFRAME_WIDTH = 1440;
const IFRAME_HEIGHT = 900;

/**
 * Aperçu live (iframe scalée) de la page de connexion de la marque blanche.
 * Récupéré de l'ancienne WhiteLabelCard : le branding V2 se gère désormais au
 * niveau du réseau, mais l'aperçu reste utile sur la fiche école.
 */
function LivePreview({ url, name }: { url: string | null; name: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);

  useEffect(() => {
    if (!containerRef.current || !url) return;
    const observer = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      setScale(width / IFRAME_WIDTH);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [url]);

  if (!url) return null;

  return (
    <div>
      <p className="text-sm font-medium text-muted-foreground mb-2">Aperçu page de connexion</p>
      <a href={url} target="_blank" rel="noopener noreferrer" className="block group">
        <div
          ref={containerRef}
          className="relative rounded-lg border overflow-hidden bg-gray-100"
          style={{ aspectRatio: `${IFRAME_WIDTH} / ${IFRAME_HEIGHT}` }}
        >
          {scale > 0 && (
            <div
              className="absolute top-0 left-0 origin-top-left"
              style={{
                width: `${IFRAME_WIDTH}px`,
                height: `${IFRAME_HEIGHT}px`,
                transform: `scale(${scale})`,
              }}
            >
              <iframe
                src={url}
                title={`Aperçu ${name}`}
                className="w-full h-full border-0 pointer-events-none"
                sandbox="allow-same-origin"
                loading="lazy"
                tabIndex={-1}
              />
            </div>
          )}
          <div className="absolute inset-0 z-10 cursor-pointer bg-transparent group-hover:bg-black/10 transition-colors flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full p-2.5 shadow-lg">
              <ExternalLink className="h-5 w-5 text-gray-700" />
            </div>
          </div>
        </div>
      </a>
    </div>
  );
}

/**
 * Carte « Réseaux » de la fiche école : réseau physique (lien fort V2,
 * définitif) + réseaux virtuels (agrégation). Remplace l'ancienne carte
 * marque blanche : la marque blanche V2 vit désormais avec le réseau
 * physique.
 */
export function NetworkCard({ school, onSaved }: NetworkCardProps) {
  const { hasPermission } = useAuth();
  const canAttach = hasPermission('action.school_networks.attach_school');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [groups, setGroups] = useState<V2TenantGroupListResponse['data']>([]);
  const [choice, setChoice] = useState<PhysicalNetworkChoice>(defaultPhysicalNetworkChoice(true));
  const [confirming, setConfirming] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const physicalNetwork = school.physicalNetwork ?? null;
  const virtualNetworks = school.virtualNetworks ?? [];

  const openDialog = async () => {
    setError(null);
    setChoice(defaultPhysicalNetworkChoice(true));
    setDialogOpen(true);
    try {
      const result = await getV2TenantGroups();
      setGroups(result.data);
    } catch (err) {
      console.error(err);
    }
  };

  const validate = (): string | null => {
    if (choice.mode === 'existing' && !choice.networkId) return 'Sélectionnez un réseau physique.';
    if (choice.mode === 'new' && (!choice.newNetworkName.trim() || !choice.newNetworkHost.trim())) {
      return 'Renseignez le nom et le domaine du nouveau réseau.';
    }
    if (!choice.schoolHost.trim()) return 'Renseignez le domaine (host) de l\'école.';
    return null;
  };

  const handleAttach = async () => {
    setConfirming(false);
    setAttaching(true);
    setError(null);
    try {
      let networkId = choice.networkId;
      if (choice.mode === 'new') {
        const created = await createSchoolNetwork({
          type: 'physical',
          name: choice.newNetworkName.trim(),
          host: choice.newNetworkHost.trim(),
        });
        networkId = created.id;
        setChoice((prev) => ({ ...prev, mode: 'existing', networkId: created.id }));
      }
      await attachSchoolToNetwork(networkId!, school.id, choice.schoolHost.trim());
      setDialogOpen(false);
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erreur lors du rattachement');
    } finally {
      setAttaching(false);
    }
  };

  const selectedNetworkName =
    choice.mode === 'new'
      ? choice.newNetworkName.trim()
      : groups.find((g) => g.networkId === choice.networkId)?.name || '';

  return (
    <Card>
      <CardHeader icon={<Network />} iconColor="purple">
        <CardTitle>Réseaux</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Réseau physique */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-muted-foreground flex items-center gap-1">
            <Server className="h-3.5 w-3.5" />
            Réseau physique
          </p>
          {physicalNetwork ? (
            <div className="rounded-lg border bg-muted/30 p-3 space-y-1">
              <Link
                to={`/school-networks/${physicalNetwork.id}`}
                className="font-medium text-primary hover:underline"
              >
                {physicalNetwork.name}
              </Link>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Globe className="h-3.5 w-3.5" />
                {school.v2Host || physicalNetwork.v2Host ? (
                  <a
                    href={`https://${school.v2Host || physicalNetwork.v2Host}`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-mono hover:underline inline-flex items-center gap-1"
                  >
                    {school.v2Host || physicalNetwork.v2Host}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <span>Host non défini</span>
                )}
              </div>
              {school.whiteLabel && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Tag className="h-3.5 w-3.5" />
                  <a
                    href={school.whiteLabel}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:underline inline-flex items-center gap-1 break-all"
                  >
                    {school.whiteLabel}
                    <ExternalLink className="h-3 w-3 shrink-0" />
                  </a>
                </div>
              )}
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Info className="h-3 w-3" />
                Rattachement définitif : l'école ne peut ni quitter ce réseau ni en changer.
              </p>
              {(school.v2Host || physicalNetwork.v2Host) && (
                <div className="pt-2">
                  <LivePreview
                    url={`https://${school.v2Host || physicalNetwork.v2Host}`}
                    name={physicalNetwork.name}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
              <p className="text-sm text-muted-foreground">
                Aucun réseau physique. Une école en marque blanche doit rejoindre un réseau
                physique avant tout déploiement V2.
              </p>
              {canAttach && (
                <Button size="sm" variant="outline" onClick={openDialog}>
                  <Link2 className="h-3.5 w-3.5" />
                  Rattacher à un réseau physique
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Réseaux virtuels */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-muted-foreground flex items-center gap-1">
            <Network className="h-3.5 w-3.5" />
            Réseaux virtuels
          </p>
          {virtualNetworks.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {virtualNetworks.map((n) => (
                <Link key={n.id} to={`/school-networks/${n.id}`}>
                  <Badge
                    variant="outline"
                    className="hover:bg-primary hover:text-primary-foreground cursor-pointer transition-colors"
                  >
                    {n.name}
                  </Badge>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Aucun réseau virtuel. Gérez les appartenances depuis la page du réseau.
            </p>
          )}
        </div>

        {/* Marque blanche V1 legacy */}
        {school.whiteLabelEntityId && !physicalNetwork && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Cette école porte une marque blanche V1 : au passage en V2 elle devra rejoindre un
              réseau physique (le groupe École Futée par défaut est interdit).
            </AlertDescription>
          </Alert>
        )}
      </CardContent>

      {/* Dialog de rattachement */}
      <Dialog open={dialogOpen} onOpenChange={(o) => !attaching && setDialogOpen(o)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-4 w-4" />
              Rattacher « {school.name} » à un réseau physique
            </DialogTitle>
            <DialogDescription>
              Le rattachement est <strong>définitif</strong> : l'école ne pourra ni être retirée du
              réseau ni déplacée.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <PhysicalNetworkSelector
              value={choice}
              onChange={setChoice}
              groups={groups}
              requiresNetwork={true}
            />
            {error && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={attaching}>
              Annuler
            </Button>
            <Button
              onClick={() => {
                const validationError = validate();
                if (validationError) {
                  setError(validationError);
                  return;
                }
                setError(null);
                setConfirming(true);
              }}
              disabled={attaching}
            >
              {attaching ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Rattachement...
                </>
              ) : (
                'Rattacher'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation irréversible */}
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Rattachement définitif
            </AlertDialogTitle>
            <AlertDialogDescription>
              {attachWarningText(
                school.name,
                selectedNetworkName || 'le réseau sélectionné',
                choice.schoolHost.trim()
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={attaching}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAttach}
              disabled={attaching}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {attaching ? 'Rattachement...' : 'Rattacher définitivement'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
